import { BrowserSessionApiClient } from "./browserSessionApiClient";
import type { AdminAccessLogRecord } from "@/types/accessLogs";

const apiClient = new BrowserSessionApiClient("/api");

export type IfcUploadSession = {
  id: string;
  fileName: string;
  blobPath: string;
  versionLabel?: string | null;
  uploadUrl: string;
  uploadUrlExpiresAt: string;
  project: {
    projectId: number;
    projectNumber: string | null;
    projectName: string | null;
    siteAddress: string | null;
  };
};

export type IfcUploadResult = {
  id: string;
  fileName: string;
  blobPath: string;
  versionLabel?: string | null;
};

export type IfcDebugViewerSession = {
  sessionToken: string;
  expiresAt: string;
  viewerPath: string;
  fileName: string;
  projectNumber: string | null;
};

export type IfcAdminFileRecord = {
  id: string;
  projectId: number | null;
  projectNumber: string | null;
  projectName: string | null;
  siteAddress: string | null;
  fileName: string;
  blobPath: string;
  fileSizeBytes: number | null;
  contentType: string | null;
  uploadedBy: string | null;
  createdAt: string;
  isDeleted: boolean;
  versionLabel: string | null;
  versionCreatedAt: string | null;
};

function getIfcContentType(file: File): string {
  return file.type || "application/octet-stream";
}

async function uploadFileToBlob(uploadUrl: string, file: File): Promise<void> {
  const response = await fetch(uploadUrl, {
    method: "PUT",
    headers: {
      "x-ms-blob-type": "BlockBlob",
      "Content-Type": getIfcContentType(file),
    },
    body: file,
  });

  if (!response.ok) {
    throw new Error(`Blob upload failed with HTTP ${response.status}.`);
  }
}

export const ifcAdminService = {
  async listFiles(projectId?: number | null): Promise<IfcAdminFileRecord[]> {
    const response = await apiClient.request<{ files: IfcAdminFileRecord[] }>("/api/ifc/files", {
      query: projectId ? { projectId } : undefined,
    });
    return Array.isArray(response.files) ? response.files : [];
  },

  async listAccessLogs(limit = 30): Promise<AdminAccessLogRecord[]> {
    const response = await apiClient.request<{ logs: AdminAccessLogRecord[] }>("/api/ifc/access-logs", {
      query: { limit },
    });
    return Array.isArray(response.logs) ? response.logs : [];
  },

  async createUploadSession(input: {
    projectId: number;
    fileName: string;
    fileSizeBytes: number;
    contentType: string;
  }): Promise<IfcUploadSession> {
    return apiClient.request<IfcUploadSession>("/api/ifc/files/upload-session", {
      method: "POST",
      body: input,
    });
  },

  async completeUpload(id: string, input: { fileSizeBytes: number; contentType: string }): Promise<{ id: string; status: string }> {
    return apiClient.request<{ id: string; status: string }>(`/api/ifc/files/${encodeURIComponent(id)}/complete-upload`, {
      method: "POST",
      body: input,
    });
  },

  async uploadIfcForProject(projectId: number, file: File): Promise<IfcUploadResult> {
    const session = await this.createUploadSession({
      projectId,
      fileName: file.name,
      fileSizeBytes: file.size,
      contentType: getIfcContentType(file),
    });

    await uploadFileToBlob(session.uploadUrl, file);
    await this.completeUpload(session.id, {
      fileSizeBytes: file.size,
      contentType: getIfcContentType(file),
    });

    return {
      id: session.id,
      fileName: session.fileName,
      blobPath: session.blobPath,
      versionLabel: session.versionLabel,
    };
  },

  async openDebugViewer(projectNumber: string, ifcFileId?: string): Promise<IfcDebugViewerSession> {
    return apiClient.request<IfcDebugViewerSession>("/api/ifc/files/debug-viewer-session", {
      method: "POST",
      body: { projectNumber, ifcFileId },
    });
  },

  async deleteFile(id: string): Promise<{ id: string; status: string }> {
    return apiClient.request<{ id: string; status: string }>(`/api/ifc/files/${encodeURIComponent(id)}`, {
      method: "DELETE",
    });
  },
};
